// src/hooks/useDeviceSettings.js

import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const DEFAULT_WATERMARK_SIZE = 'medium';
const DEFAULT_WATERMARK_POSITION = 'top_right';
const DEFAULT_YOUTUBE_PLAYER_COMPATIBILITY_MODE = 'off';

const useDeviceSettings = (device) => {
  const deviceId = device?.id || null;
  const selectionRef = useRef({ deviceId });
  if (selectionRef.current.deviceId !== deviceId) selectionRef.current = { deviceId };
  const saveRef = useRef(null);
  const mountedRef = useRef(true);
  const [reload, setReload] = useState(0);
  const [overlayPauseState, setOverlayPauseState] = useState({ deviceId: null, enabled: false, loading: true });
  const [fadeDuration, setFadeDuration] = useState(2); // Fade duration in seconds
  const [watermarkSize, setWatermarkSize] = useState(DEFAULT_WATERMARK_SIZE);
  const [watermarkPosition, setWatermarkPosition] = useState(DEFAULT_WATERMARK_POSITION);
  const [youtubePlayerCompatibilityMode, setYoutubePlayerCompatibilityMode] = useState(
    DEFAULT_YOUTUBE_PLAYER_COMPATIBILITY_MODE
  );

  useEffect(() => {
    let active = true;
    const selection = selectionRef.current;
    setFadeDuration(2);
    setWatermarkSize(DEFAULT_WATERMARK_SIZE);
    setWatermarkPosition(DEFAULT_WATERMARK_POSITION);
    setYoutubePlayerCompatibilityMode(DEFAULT_YOUTUBE_PLAYER_COMPATIBILITY_MODE);
    setOverlayPauseState({ deviceId, enabled: false, loading: !!deviceId, saving: false, error: '', warning: '' });
    if (!deviceId) {
      return () => { active = false; };
    }

    const fetchDeviceSettings = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/devices/get-device-settings', {
          params: { deviceId },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!active || selectionRef.current !== selection) return;
        const settings = response.data.settings;
        if (!settings || response.data.success === false) throw new Error('Could not load device settings');
        if (settings.fade_duration !== null && settings.fade_duration !== undefined) {
          setFadeDuration(settings.fade_duration);
        }
        setWatermarkSize(settings.watermark_size || DEFAULT_WATERMARK_SIZE);
        setWatermarkPosition(settings.watermark_position || DEFAULT_WATERMARK_POSITION);
        setYoutubePlayerCompatibilityMode(
          settings.youtube_player_compatibility_mode || DEFAULT_YOUTUBE_PLAYER_COMPATIBILITY_MODE
        );
        setOverlayPauseState({ deviceId, enabled: settings.pause_background_video_on_video_overlay === true
          || settings.pause_background_video_on_video_overlay === 1,
        loading: false, saving: false, error: '', warning: '' });
      } catch (error) {
        if (!active || selectionRef.current !== selection) return;
        setOverlayPauseState({ deviceId, enabled: false, loading: false, saving: false,
          error: 'Unable to load device settings. Retry before changing this setting.', warning: '', loadFailed: true });
        console.error('Error fetching device settings:', error);
      }
    };

    fetchDeviceSettings();
    return () => { active = false; };
  }, [deviceId, reload]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const saveVideoOverlayPauseSetting = async (enabled) => {
    const selection = selectionRef.current;
    if (typeof enabled !== 'boolean' || !deviceId || overlayPauseState.deviceId !== deviceId
      || overlayPauseState.loading || overlayPauseState.loadFailed || saveRef.current === selection) return;
    saveRef.current = selection;
    setOverlayPauseState((state) => ({ ...state, saving: true, error: '', warning: '' }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/devices/update-settings', {
        deviceId, settings: { pause_background_video_on_video_overlay: enabled },
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data?.success !== true) throw new Error('Save rejected');
      if (!mountedRef.current || selectionRef.current !== selection) return;
      setOverlayPauseState({ deviceId, enabled, loading: false, saving: false, error: '',
        warning: response.data.liveUpdateDelivered === false
          ? 'Saved. The player will apply this setting after reconnecting.' : '' });
    } catch (error) {
      if (!mountedRef.current || selectionRef.current !== selection) return;
      setOverlayPauseState((state) => ({ ...state, saving: false,
        error: 'Unable to save this setting. Your previous value is still selected.' }));
    } finally {
      if (saveRef.current === selection) saveRef.current = null;
    }
  };

  const updateDeviceSetting = async (settingName, value) => {
    if (!deviceId) return;
    try {
      const token = localStorage.getItem('token');
      const payload = {
        deviceId,
        settings: { [settingName]: value },
      };
      await axios.post('/api/devices/update-settings', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error(`Error updating device setting ${settingName}:`, error);
    }
  };

  // Enforce the constraint that autoloadDelay >= mixTime + preloadAmount
  //useEffect(() => {
  //  const minAutoloadDelay = mixTime + preloadAmount;
  //  if (autoloadDelay < minAutoloadDelay) {
  //    setAutoloadDelay(minAutoloadDelay);
  //    updateDeviceSetting('autoload_delay', minAutoloadDelay);
  //  }
  //}, [mixTime, preloadAmount]);

  return {
    fadeDuration,
    setFadeDuration,
    watermarkSize,
    setWatermarkSize,
    watermarkPosition,
    setWatermarkPosition,
    youtubePlayerCompatibilityMode,
    setYoutubePlayerCompatibilityMode,
    updateDeviceSetting,
    pauseBackgroundVideoOnVideoOverlay: overlayPauseState.deviceId === deviceId && overlayPauseState.enabled === true,
    overlayPauseSettingLoading: overlayPauseState.deviceId !== deviceId || overlayPauseState.loading,
    overlayPauseSettingSaving: overlayPauseState.deviceId === deviceId && overlayPauseState.saving === true,
    overlayPauseSettingError: overlayPauseState.deviceId === deviceId ? overlayPauseState.error : '',
    overlayPauseSettingWarning: overlayPauseState.deviceId === deviceId ? overlayPauseState.warning : '',
    overlayPauseSettingLoadFailed: overlayPauseState.deviceId === deviceId && overlayPauseState.loadFailed === true,
    saveVideoOverlayPauseSetting,
    reloadDeviceSettings: () => setReload((value) => value + 1),
  };
};

export default useDeviceSettings;
